/**
 * Asistente flotante "Pixel": botón arrastrable con contador que vive en el menú lateral,
 * arriba de «Cerrar sesión» (hueco `#cm-pixel-dock` de MainLayout). Al iniciar sesión, al volver
 * a la pantalla y en los recordatorios muestra un solo mensaje en un globo; la conversación
 * completa solo se abre al darle clic. Si no se usa (o se cierra) se duerme como icono con
 * "Zzz". Panel de conversación por reglas (assistantBrain.js) que entiende
 * filtros, recuerda la última lista ("el segundo") y puede aprobar, completar o validar
 * con confirmación. Lee pendientes de GET /assistant/digest/:uid y navega con NavigationContext.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../AuthContext';
import { useData } from '../../DataContext';
import { useNavigation } from '../../NavigationContext';
import { ink } from '../admin/studioUi';
import { NARROW_MAX } from '../../useMediaQuery';
import PixelMascot, { PIXEL_CSS } from './PixelMascot';
import {
    EYE_OFFSET, LAUNCHER_SIZE, clampPosition, defaultPosition, dockPosition, gazeFor, nearDock, placePopover,
    restPosition, thoughtPlacement,
} from './pixelPlacement';
import {
    approvalProgress,
    bumpUsage,
    decideNudge,
    describeFilters,
    detectIntent,
    filterAdjustments,
    filterApprovals,
    firstName,
    greetingFor,
    dayKey,
    hasFilters,
    parseFilters,
    parseReference,
    pendingTotal,
    pickFromList,
    priorityItems,
    relativeDeadline,
    reminderText,
    searchReports,
    summaryLines,
    topUsed,
    urgentItems,
} from './assistantBrain';

const POLL_MS = 10 * 60 * 1000;
const STALE_MS = 2 * 60 * 1000;
const NUDGE_AUTOHIDE_MS = 20 * 1000;
/* Animación al recibir una instrucción: anota (teclea) y luego piensa (nube con engranes). */
const TYPING_MS = 450;
const THINK_MIN_MS = 1000;
const PICK_MS = 320;
const USER_TYPING_IDLE_MS = 1200;
/* Saluda con un globo al volver a la pantalla tras estar fuera al menos esto. */
export const RETURN_GREET_MS = 15 * 1000;
/* Sin interacción durante esto, cierra la conversación y se duerme. */
export const IDLE_SLEEP_MS = 90 * 1000;
const IDLE_CHECK_MS = 5 * 1000;

const wait = (ms) => new Promise((resolve) => { window.setTimeout(resolve, ms); });

const QUICK_CHIPS = [
    '¿Qué hago primero?',
    'Mis pendientes',
    'Reportes por aprobar',
    'Mis ajustes',
    'Ajustes por validar',
    'Menciones',
    'Mis frecuentes',
];

const storageKey = (uid) => `cm-pixel:${uid}`;
const usageKey = (uid) => `cm-pixel-usage:${uid}`;
/* v2: Pixel se mudó al menú lateral; las posiciones guardadas de antes ya no aplican. */
const POSITION_KEY = 'cm-pixel-pos-v2';
const DOCK_ID = 'cm-pixel-dock';
/* En móvil se recuerda otra posición: la de escritorio no tiene sentido en una pantalla angosta. */
const positionKey = () => (window.innerWidth <= NARROW_MAX ? `${POSITION_KEY}-m` : POSITION_KEY);
const DRAG_THRESHOLD = 5;
const KEY_STEP = 24;

const viewportSize = () => ({ w: window.innerWidth, h: window.innerHeight });

/** Posición a la que el usuario arrastró a Pixel, o null si sigue en su lugar del menú. */
function readPosition() {
    const { w, h } = viewportSize();
    try {
        const saved = JSON.parse(window.localStorage.getItem(positionKey()) || 'null');
        if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) return clampPosition(saved, w, h);
    } catch (error) {
        /* Sin storage: se queda en su lugar del menú. */
    }
    return null;
}

/** Guarda la posición arrastrada; con null la olvida y Pixel vuelve a su lugar del menú. */
function writePosition(pos) {
    try {
        if (pos) window.localStorage.setItem(positionKey(), JSON.stringify(pos));
        else window.localStorage.removeItem(positionKey());
    } catch (error) {
        /* Sin storage (modo privado): solo no recuerda dónde quedó. */
    }
}

function readUsage(uid) {
    try {
        return JSON.parse(window.localStorage.getItem(usageKey(uid)) || '{}') || {};
    } catch (error) {
        return {};
    }
}

function writeUsage(uid, usage) {
    try {
        window.localStorage.setItem(usageKey(uid), JSON.stringify(usage));
    } catch (error) {
        /* Sin storage: la búsqueda funciona igual, solo no aprende. */
    }
}

function readNudgeState(uid) {
    try {
        return JSON.parse(window.localStorage.getItem(storageKey(uid)) || '{}') || {};
    } catch (error) {
        return {};
    }
}

function writeNudgeState(uid, patch) {
    try {
        const next = { ...readNudgeState(uid), ...patch };
        window.localStorage.setItem(storageKey(uid), JSON.stringify(next));
    } catch (error) {
        /* Sin storage (modo privado): la mascota sigue funcionando, solo no recuerda. */
    }
}

const truncate = (text, max = 110) => {
    const clean = String(text || '').replace(/\s+/g, ' ').trim();
    return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
};

const PANEL_CSS = `
.cm-pixel-launcher { transition: transform 0.18s ease; }
.cm-pixel-launcher:hover { transform: translateY(-2px) scale(1.04); }
.cm-pixel-launcher:focus-visible, .cm-pixel-chip:focus-visible, .cm-pixel-act:focus-visible {
  outline: 2px solid ${ink.accent};
  outline-offset: 2px;
}
.cm-pixel-chip:hover, .cm-pixel-act:hover { filter: brightness(0.94); }
.cm-pixel-chips { scrollbar-width: none; }
.cm-pixel-chips::-webkit-scrollbar { display: none; }
.cm-pixel-pop { animation: cm-pixel-pop 0.22s ease-out; }
.cm-pixel-thinking::after { content: ''; animation: cm-pixel-dots 1.2s steps(1) infinite; }
@keyframes cm-pixel-dots { 0% { content: ''; } 25% { content: '.'; } 50% { content: '..'; } 75% { content: '...'; } }
@keyframes cm-pixel-pop { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .cm-pixel-pop, .cm-pixel-launcher { animation: none; transition: none; } }
`;

export default function AssistantBuddy() {
    const { user, userData, userPermissions, impersonation, fetchWithAuth, API_URL } = useAuth();
    const { dashboards, pendingApprovalReports, setNotifications, unreadNotificationsCount, refreshAllData } = useData();
    const { view, adminSubView, selectedReportId, openReport, goHome, setView, setAdminSubView, handleViewReportDetailsById, handleNotificationNavigation } = useNavigation();

    const uid = impersonation ? impersonation.uid : user?.uid;
    const displayName = impersonation ? '' : firstName(userData?.nombre);

    const [open, setOpen] = useState(false);
    const [digest, setDigest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [nudge, setNudge] = useState(null);
    const [pos, setPos] = useState(() => readPosition() || defaultPosition(window.innerWidth, window.innerHeight));
    /* En su lugar del menú (arriba de «Cerrar sesión») mientras el usuario no lo arrastre a otro lado. */
    const [docked, setDocked] = useState(() => readPosition() == null);
    const [dock, setDock] = useState({ at: null, sideRight: 0 });
    const [viewport, setViewport] = useState(viewportSize);
    const [dragging, setDragging] = useState(false);
    const dragRef = useRef(null);
    const suppressClickRef = useRef(false);
    const [activity, setActivity] = useState(null);
    const [userTyping, setUserTyping] = useState(false);
    const [gaze, setGaze] = useState({ x: 0, y: 0 });
    const typingTimerRef = useRef(null);
    const lastInteractionRef = useRef(Date.now());
    const greetedRef = useRef(false);
    const greetRef = useRef(null);
    const awaySinceRef = useRef(null);
    const mountedRef = useRef(true);
    const posRef = useRef(pos);
    posRef.current = pos;

    const [usage, setUsage] = useState({});
    const lastListRef = useRef(null);
    const lastFetchRef = useRef(0);
    const msgIdRef = useRef(0);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const openRef = useRef(open);
    openRef.current = open;

    const allReports = useMemo(() => {
        const map = new Map();
        [...(dashboards || []), ...(pendingApprovalReports || [])].forEach((report) => {
            if (report?.id && !map.has(report.id)) map.set(report.id, report);
        });
        return [...map.values()];
    }, [dashboards, pendingApprovalReports]);

    const clients = useMemo(
        () => [...new Set(allReports.map((report) => report.cliente).filter(Boolean))],
        [allReports]
    );

    /* Aprende qué reportes abre el usuario (en Pixel o en el menú) para priorizarlos al buscar. */
    useEffect(() => {
        if (uid) setUsage(readUsage(uid));
    }, [uid]);

    useEffect(() => {
        if (!uid || !selectedReportId) return;
        setUsage((prev) => {
            const next = bumpUsage(prev, selectedReportId);
            writeUsage(uid, next);
            return next;
        });
    }, [uid, selectedReportId]);

    const pushMessage = useCallback((message) => {
        msgIdRef.current += 1;
        const id = msgIdRef.current;
        setMessages((prev) => [...prev.slice(-40), { id, ...message }]);
        return id;
    }, []);

    /* ------------------------------------------------------------------ */
    /* Datos                                                               */
    /* ------------------------------------------------------------------ */

    const fetchDigest = useCallback(async () => {
        if (!uid) return null;
        setLoading(true);
        try {
            const res = await fetchWithAuth(`${API_URL}/assistant/digest/${uid}`, { silent: true });
            const data = await res.json();
            if (!data.success) return null;
            lastFetchRef.current = Date.now();
            setDigest(data);
            return data;
        } catch (error) {
            console.error('Pixel: no se pudo leer el resumen:', error);
            return null;
        } finally {
            setLoading(false);
        }
    }, [uid, fetchWithAuth, API_URL]);

    /** Evalúa si hay que saludar o recordar y muestra el globo. */
    const evaluateNudge = useCallback((data) => {
        if (!data || !uid || openRef.current) return;
        const state = readNudgeState(uid);
        const now = new Date();
        const decision = decideNudge(data, state, now);
        if (!decision.kind) return;

        if (decision.kind === 'greet') {
            const total = pendingTotal(data);
            const hello = `${greetingFor(now)}${displayName ? `, ${displayName}` : ''}!`;
            setNudge({
                mood: 'wave',
                text: total
                    ? `${hello} Tienes ${total} ${total === 1 ? 'pendiente' : 'pendientes'}. ¿Te los muestro?`
                    : `${hello} Estás al día. Si buscas un reporte, pregúntame.`,
            });
            writeNudgeState(uid, { lastGreetDay: dayKey(now), lastRemindAt: now.getTime(), lastSignature: decision.signature });
        } else {
            setNudge({ mood: 'alert', text: reminderText(data, now) });
            writeNudgeState(uid, { lastRemindAt: now.getTime(), lastSignature: decision.signature });
        }
    }, [uid, displayName]);

    useEffect(() => {
        if (!uid) return undefined;
        let cancelled = false;
        const tick = async () => {
            const data = await fetchDigest();
            if (cancelled) return;
            /* Al iniciar sesión Pixel saluda con un globo; después solo recordatorios. */
            if (!greetedRef.current) {
                greetedRef.current = true;
                greetRef.current?.(data, 'login');
                return;
            }
            evaluateNudge(data);
        };
        tick();
        const timer = window.setInterval(tick, POLL_MS);

        const leave = () => {
            if (awaySinceRef.current == null) awaySinceRef.current = Date.now();
        };
        const comeBack = async () => {
            const since = awaySinceRef.current;
            awaySinceRef.current = null;
            if (since != null && Date.now() - since >= RETURN_GREET_MS) {
                const data = await fetchDigest();
                /* mountedRef (no `cancelled`): el saludo no debe perderse si el efecto se re-suscribe. */
                if (mountedRef.current) greetRef.current?.(data, 'return');
            } else if (Date.now() - lastFetchRef.current > STALE_MS) {
                tick();
            }
        };
        const onVisibility = () => (document.visibilityState === 'hidden' ? leave() : comeBack());
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('blur', leave);
        window.addEventListener('focus', comeBack);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('blur', leave);
            window.removeEventListener('focus', comeBack);
        };
    }, [uid, fetchDigest, evaluateNudge]);

    /* Si el usuario lee notificaciones en la campana, refrescamos el contador. */
    const lastUnreadRef = useRef(unreadNotificationsCount);
    useEffect(() => {
        if (lastUnreadRef.current === unreadNotificationsCount) return;
        lastUnreadRef.current = unreadNotificationsCount;
        fetchDigest();
    }, [unreadNotificationsCount, fetchDigest]);

    useEffect(() => {
        if (!nudge) return undefined;
        const timer = window.setTimeout(() => setNudge(null), NUDGE_AUTOHIDE_MS);
        return () => window.clearTimeout(timer);
    }, [nudge]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open, activity]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    /* Al cambiar el tamaño de la ventana, Pixel no se queda fuera de la pantalla. */
    useEffect(() => {
        const onResize = () => {
            const next = viewportSize();
            setViewport(next);
            setPos((prev) => clampPosition(prev, next.w, next.h));
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (!dragging) writePosition(docked ? null : pos);
    }, [pos, dragging, docked]);

    /* Mide el hueco del menú lateral: cambia al ocultar el menú, al hacer scroll en él o al redimensionar. */
    useEffect(() => {
        let frame = 0;
        let timer = 0;
        let lastKey = '0|';
        const measure = () => {
            const slot = document.getElementById(DOCK_ID);
            const side = slot ? slot.closest('[data-cm-sidebar]') : null;
            const sideRect = side ? side.getBoundingClientRect() : null;
            const at = slot ? dockPosition(slot.getBoundingClientRect(), window.innerWidth, window.innerHeight) : null;
            const sideRight = sideRect && sideRect.width > 1 && sideRect.right > 0 ? Math.round(sideRect.right) : 0;
            const key = `${sideRight}|${at ? `${at.x},${at.y}` : ''}`;
            if (key === lastKey) return;
            lastKey = key;
            setDock({ at, sideRight });
        };
        const soon = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(measure);
        };
        /* Un clic puede plegar el menú o abrir un grupo: se vuelve a medir al terminar la transición. */
        const later = () => {
            soon();
            window.clearTimeout(timer);
            timer = window.setTimeout(measure, 340);
        };
        measure();
        const slot = document.getElementById(DOCK_ID);
        const observer = typeof ResizeObserver === 'undefined' || !slot ? null : new ResizeObserver(soon);
        if (observer) observer.observe(slot);
        window.addEventListener('resize', soon);
        document.addEventListener('scroll', soon, true);
        document.addEventListener('click', later, true);
        return () => {
            window.cancelAnimationFrame(frame);
            window.clearTimeout(timer);
            if (observer) observer.disconnect();
            window.removeEventListener('resize', soon);
            document.removeEventListener('scroll', soon, true);
            document.removeEventListener('click', later, true);
        };
    }, [view, adminSubView]);

    /* Mientras no lo arrastren, Pixel sigue a su hueco; si el hueco no está a la vista, espera a un lado. */
    useEffect(() => {
        if (!docked || dragging) return;
        /* En móvil el menú es un cajón que queda encima de Pixel: ahí no se ancla. */
        const narrowNow = viewport.w <= NARROW_MAX;
        const target = (!narrowNow && dock.at) || restPosition({
            narrow: narrowNow,
            sideRight: dock.sideRight,
            vw: viewport.w,
            vh: viewport.h,
        });
        setPos((prev) => (prev.x === target.x && prev.y === target.y ? prev : target));
    }, [docked, dragging, dock, viewport]);

    /* Pixel sigue al cursor con la mirada (una actualización por cuadro, solo si cambia). */
    useEffect(() => {
        let frame = 0;
        let last = null;
        const onMove = (event) => {
            last = event;
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                const p = posRef.current;
                const next = gazeFor(last.clientX - (p.x + EYE_OFFSET.x), last.clientY - (p.y + EYE_OFFSET.y));
                setGaze((prev) => (prev.x === next.x && prev.y === next.y ? prev : next));
            });
        };
        window.addEventListener('pointermove', onMove);
        return () => {
            window.removeEventListener('pointermove', onMove);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            window.clearTimeout(typingTimerRef.current);
        };
    }, []);

    /** Registra que el usuario está usando a Pixel (reinicia la cuenta para dormirse). */
    const markInteraction = () => {
        lastInteractionRef.current = Date.now();
    };

    /** Mientras el usuario escribe, Pixel "toma nota" en su laptop. */
    const onInputChange = (event) => {
        markInteraction();
        setInput(event.target.value);
        setUserTyping(true);
        window.clearTimeout(typingTimerRef.current);
        typingTimerRef.current = window.setTimeout(() => setUserTyping(false), USER_TYPING_IDLE_MS);
    };

    /** Al elegir un botón de una tarjeta, Pixel anota la instrucción y luego actúa. */
    const pick = useCallback((fn) => {
        lastInteractionRef.current = Date.now();
        setActivity('typing');
        window.setTimeout(() => {
            setActivity(null);
            fn();
        }, PICK_MS);
    }, []);

    /* ------------------------------------------------------------------ */
    /* Arrastre                                                            */
    /* ------------------------------------------------------------------ */

    const onPointerDown = (event) => {
        if (event.button !== 0) return;
        dragRef.current = { startX: event.clientX, startY: event.clientY, origin: pos, moved: false };
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        if (!drag.moved) {
            drag.moved = true;
            setDragging(true);
        }
        setPos(clampPosition({ x: drag.origin.x + dx, y: drag.origin.y + dy }, viewport.w, viewport.h));
    };

    const onPointerUp = (event) => {
        const drag = dragRef.current;
        dragRef.current = null;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        if (drag?.moved) {
            suppressClickRef.current = true;
            /* Soltarlo cerca de su hueco lo devuelve al menú; en otro lado se queda donde lo dejaron. */
            setDocked(nearDock(posRef.current, dock.at));
            setDragging(false);
        }
    };

    /** Flechas del teclado mueven a Pixel (accesible sin mouse). */
    const onLauncherKeyDown = (event) => {
        const moves = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
        const move = moves[event.key];
        if (!move) return;
        event.preventDefault();
        setDocked(false);
        setPos((prev) => clampPosition({ x: prev.x + move[0] * KEY_STEP, y: prev.y + move[1] * KEY_STEP }, viewport.w, viewport.h));
    };

    /* ------------------------------------------------------------------ */
    /* Acciones de navegación                                              */
    /* ------------------------------------------------------------------ */

    const goTo = useCallback((fn) => {
        setOpen(false);
        fn();
    }, []);

    const openReportById = useCallback((reportId) => {
        const report = allReports.find((row) => row.id === reportId);
        goTo(() => (report ? openReport(report) : handleViewReportDetailsById(reportId)));
    }, [allReports, goTo, openReport, handleViewReportDetailsById]);

    const openDetails = useCallback((reportId) => {
        goTo(() => handleViewReportDetailsById(reportId));
    }, [goTo, handleViewReportDetailsById]);

    const markRead = useCallback(async (notificationId) => {
        if (impersonation) return;
        try {
            await fetchWithAuth(`${API_URL}/notifications/${notificationId}/read`, { method: 'PUT', silent: true });
            setNotifications((prev) => prev.map((row) => (row.id === notificationId ? { ...row, read: true } : row)));
        } catch (error) {
            console.error('Pixel: no se pudo marcar como leída:', error);
        }
    }, [impersonation, fetchWithAuth, API_URL, setNotifications]);

    const openNotification = useCallback((notification) => {
        markRead(notification.id);
        goTo(() => handleNotificationNavigation(notification));
    }, [markRead, goTo, handleNotificationNavigation]);

    /* ------------------------------------------------------------------ */
    /* Respuestas                                                          */
    /* ------------------------------------------------------------------ */

    /** Quita los botones de un mensaje (p. ej. una confirmación ya respondida). */
    const consumeMessage = useCallback((id) => {
        setMessages((prev) => prev.map((row) => (row.id === id ? { ...row, cards: undefined, actions: undefined } : row)));
    }, []);

    /** Recuerda la última lista mostrada para "ábrelo", "el segundo", "aprueba el 1". */
    const rememberList = (kind, items) => {
        lastListRef.current = { kind, items };
    };

    const numbered = (cards) => cards.map((card, index) => (
        cards.length > 1 ? { ...card, title: `${index + 1}. ${card.title}` } : card
    ));

    const approvalCard = (row) => ({
        key: `ap-${row.responsibleId}`,
        tone: row.overdue ? 'danger' : row.dueSoon ? 'warn' : 'plain',
        title: row.reportName,
        subtitle: [row.area && `Área ${row.area}`, relativeDeadline(row.deadline), approvalProgress(row), row.locked && 'Bloqueado por admin']
            .filter(Boolean).join(' · '),
        actions: [
            ...(!impersonation && !row.locked ? [{ label: 'Aprobar', onClick: () => askApprove(row) }] : []),
            { label: 'Revisar', onClick: () => openDetails(row.reportId) },
        ],
    });

    const adjustmentCard = (row, kind) => ({
        key: `${kind}-${row.id}`,
        tone: 'plain',
        title: row.reportName,
        subtitle: kind === 'validate'
            ? `“${truncate(row.text, 90)}” · completado por ${row.completedBy || 'alguien'}`
            : `“${truncate(row.text, 90)}” · pidió ${row.requestedBy || 'alguien'}`,
        actions: [
            ...(!impersonation ? [{
                label: kind === 'validate' ? 'Validar' : 'Marcar completado',
                onClick: () => askAdjustment(row, kind === 'validate' ? 'validate' : 'complete'),
            }] : []),
            { label: 'Ver ajuste', onClick: () => openDetails(row.reportId) },
        ],
    });

    const notificationCard = (row) => ({
        key: `nt-${row.id}`,
        tone: row.type === 'user_mention_in_comment' ? 'accent' : 'plain',
        title: truncate(row.message, 120),
        subtitle: row.timestamp ? new Date(row.timestamp).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '',
        actions: [
            ...(row.entityId || row.module === 'Groups' ? [{ label: 'Ir', onClick: () => openNotification(row) }] : []),
            ...(!impersonation ? [{ label: 'Marcar leída', onClick: () => { markRead(row.id); } }] : []),
        ],
    });

    const reportCard = (report) => ({
        key: `rp-${report.id}`,
        tone: 'plain',
        title: report.reporteNombre || 'Reporte sin nombre',
        subtitle: [report.cliente, report.proyecto].filter(Boolean).join(' · '),
        actions: [
            { label: 'Abrir', onClick: () => openReportById(report.id) },
            { label: 'Detalles', onClick: () => openDetails(report.id) },
        ],
    });

    /** Tarjeta para un elemento de "¿qué hago primero?". */
    const priorityCard = ({ kind, row, reason }) => {
        if (kind === 'approval') return { ...approvalCard(row), subtitle: reason };
        if (kind === 'mention') return notificationCard(row);
        return adjustmentCard(row, kind === 'validate' ? 'validate' : 'assigned');
    };

    /** Ejecuta una acción del backend tras la confirmación y refresca los pendientes. */
    const runAction = useCallback(async (confirmId, request, successText) => {
        consumeMessage(confirmId);
        setActivity('thinking');
        try {
            await fetchWithAuth(request.url, { method: request.method, body: request.body ? JSON.stringify(request.body) : undefined });
            pushMessage({ from: 'bot', text: successText });
            refreshAllData();
            fetchDigest();
        } catch (error) {
            pushMessage({ from: 'bot', text: `No se pudo: ${error.message}` });
        } finally {
            setActivity(null);
        }
    }, [consumeMessage, fetchWithAuth, pushMessage, refreshAllData, fetchDigest]);

    /** Pide confirmación antes de aprobar un reporte como responsable. */
    const askApprove = (row) => {
        if (impersonation) {
            pushMessage({ from: 'bot', text: 'Mientras ves la app como otra persona no hago cambios por ella. Sal de la suplantación para aprobar.' });
            return;
        }
        if (row.locked) {
            pushMessage({ from: 'bot', text: `La aprobación de “${row.reportName}” está bloqueada por un administrador.` });
            return;
        }
        const id = pushMessage({
            from: 'bot',
            text: `¿Apruebo “${row.reportName}”${row.area ? ` como responsable de ${row.area}` : ''}?${row.overdue ? ' El deadline ya venció: quedará registrada como aprobación tardía.' : ''}`,
            actions: [
                {
                    label: 'Sí, aprobar',
                    primary: true,
                    onClick: () => runAction(id, {
                        method: 'PUT',
                        url: `${API_URL}/admin/reportes-maestros/${row.reportId}/responsibles/${row.responsibleId}/approve`,
                        body: { approved: true, acknowledgeLate: row.overdue === true },
                    }, `Listo, aprobé “${row.reportName}”. ✅`),
                },
                { label: 'Cancelar', onClick: () => { consumeMessage(id); pushMessage({ from: 'bot', text: 'Va, no aprobé nada.' }); } },
            ],
        });
    };

    /** Pide confirmación antes de completar o validar un ajuste. */
    const askAdjustment = (row, action) => {
        if (impersonation) {
            pushMessage({ from: 'bot', text: 'Mientras ves la app como otra persona no hago cambios por ella.' });
            return;
        }
        const verb = action === 'validate' ? 'valido' : 'marco como completado';
        const done = action === 'validate' ? 'Validé' : 'Marqué como completado';
        const id = pushMessage({
            from: 'bot',
            text: `¿${verb.charAt(0).toUpperCase()}${verb.slice(1)} el ajuste “${truncate(row.text, 80)}” de “${row.reportName}”?`,
            actions: [
                {
                    label: action === 'validate' ? 'Sí, validar' : 'Sí, completar',
                    primary: true,
                    onClick: () => runAction(id, {
                        method: 'POST',
                        url: `${API_URL}/admin/reportes-maestros/${row.reportId}/adjustments/${row.id}/${action}`,
                    }, `${done} el ajuste de “${row.reportName}”. ✅`),
                },
                { label: 'Cancelar', onClick: () => { consumeMessage(id); pushMessage({ from: 'bot', text: 'Va, lo dejo como estaba.' }); } },
            ],
        });
    };

    /**
     * Resuelve a qué elemento se refiere una acción: primero por "el 2"/"ese" sobre la
     * última lista, luego por filtros ("el de Besins"). Si hay varios, los muestra y pregunta.
     */
    const resolveTarget = (text, rows, { kindOfList, sameItem, filter, listCards, label }) => {
        const ref = parseReference(text);
        const last = lastListRef.current;
        if ((ref.ordinal != null || ref.pronoun) && last?.items?.length) {
            const picked = pickFromList(last.items, ref);
            const match = picked && rows.find((row) => sameItem(row, picked, last.kind));
            if (match) return match;
            if (picked) {
                pushMessage({ from: 'bot', text: `Ese elemento no es ${label}. Pídeme “${kindOfList}” para ver cuáles son.` });
                return null;
            }
        }
        const candidates = filter(rows);
        if (candidates.length === 1) return candidates[0];
        if (!candidates.length) {
            pushMessage({ from: 'bot', text: rows.length ? `No encontré ${label} con eso. Pídeme “${kindOfList}” para ver la lista.` : `No tienes ${label} pendientes.` });
            return null;
        }
        rememberList(listCards.kind, candidates);
        pushMessage({
            from: 'bot',
            text: `Tienes ${candidates.length}. ¿Cuál? Dime, por ejemplo, “${listCards.example} el 2”.`,
            cards: numbered(candidates.map(listCards.card)),
        });
        return null;
    };

    const reply = useCallback(async (text) => {
        const intent = detectIntent(text);
        const needsDigest = !['help', 'account', 'home', 'creator', 'search', 'favorites', 'open_ref'].includes(intent);
        let data = digest;
        if (needsDigest && (!data || Date.now() - lastFetchRef.current > STALE_MS)) {
            data = (await fetchDigest()) || digest;
        }
        if (needsDigest && !data) {
            pushMessage({ from: 'bot', text: 'Uy, no pude revisar tus pendientes ahorita. Intenta de nuevo en un momento.' });
            return;
        }
        const filters = parseFilters(text, { clients });
        const clientOf = (reportId) => allReports.find((report) => report.id === reportId)?.cliente || null;
        const filtered = hasFilters(filters) ? ` ${describeFilters(filters)}` : '';

        switch (intent) {
            case 'help':
                pushMessage({
                    from: 'bot',
                    text: 'Soy Pixel. Puedo:',
                    lines: [
                        'Buscar reportes: “ventas norte”, “besins agosto”.',
                        'Filtrar pendientes: “¿qué vence esta semana?”, “ajustes de Besins”, “vencidos”.',
                        'Decirte qué hacer primero: “¿por dónde empiezo?”.',
                        'Actuar por ti (con confirmación): “aprueba el 1”, “completa el ajuste de Grin”, “valida ese”.',
                        'Abrir lo de la lista anterior: “ábrelo”, “el segundo”, “el último”.',
                        'Mostrar tus reportes más usados: “mis frecuentes”.',
                    ],
                });
                break;
            case 'summary': {
                const lines = summaryLines(data);
                const top = priorityItems(data, new Date(), 1)[0];
                pushMessage({
                    from: 'bot',
                    text: lines.length ? 'Esto es lo que tienes pendiente:' : '¡Estás al día! No tienes pendientes. 🎉',
                    lines: top ? [...lines, `Lo más urgente: ${top.row.reportName || 'una mención'}${top.kind === 'approval' ? ` (${top.reason})` : ''}. Pregúntame “¿qué hago primero?”.`] : lines,
                });
                break;
            }
            case 'priority': {
                const items = priorityItems(data);
                rememberList('priority', items.map((item) => ({ ...item.row, kind: item.kind })));
                pushMessage({
                    from: 'bot',
                    text: items.length ? 'En este orden, de lo más urgente a lo menos:' : '¡Nada urgente! Estás al día. 🎉',
                    cards: numbered(items.map(priorityCard)),
                });
                break;
            }
            case 'approvals': {
                const rows = filterApprovals(data.approvals, filters);
                rememberList('approvals', rows);
                pushMessage({
                    from: 'bot',
                    text: rows.length
                        ? `Tienes ${rows.length} ${rows.length === 1 ? 'reporte' : 'reportes'} por aprobar${filtered}. Los vencidos van primero:`
                        : `No tienes reportes por aprobar${filtered}. ✅`,
                    cards: numbered(rows.map(approvalCard)),
                });
                break;
            }
            case 'adjustments': {
                const rows = filterAdjustments(data.adjustmentsAssigned, filters, clientOf);
                const toValidate = data.counts?.adjustmentsToValidate || 0;
                rememberList('assigned', rows);
                pushMessage({
                    from: 'bot',
                    text: rows.length
                        ? `Tienes ${rows.length} ${rows.length === 1 ? 'ajuste asignado' : 'ajustes asignados'} por resolver${filtered}:`
                        : `No tienes ajustes asignados pendientes${filtered}.`,
                    cards: numbered(rows.map((row) => adjustmentCard(row, 'assigned'))),
                    lines: toValidate ? [`Además hay ${toValidate} por validar. Pregúntame “ajustes por validar”.`] : undefined,
                });
                break;
            }
            case 'validate': {
                const rows = filterAdjustments(data.adjustmentsToValidate, filters, clientOf);
                rememberList('validate', rows);
                pushMessage({
                    from: 'bot',
                    text: rows.length ? `Estos ajustes ya se completaron y esperan tu validación${filtered}:` : `No hay ajustes esperando tu validación${filtered}.`,
                    cards: numbered(rows.map((row) => adjustmentCard(row, 'validate'))),
                });
                break;
            }
            case 'approve_action': {
                const row = resolveTarget(text, data.approvals || [], {
                    kindOfList: 'reportes por aprobar',
                    label: 'un reporte por aprobar',
                    sameItem: (candidate, picked, kind) => (kind === 'approvals' || picked.kind === 'approval'
                        ? candidate.responsibleId === picked.responsibleId
                        : candidate.reportId === (picked.reportId || picked.id)),
                    filter: (rows) => filterApprovals(rows, filters),
                    listCards: { kind: 'approvals', card: approvalCard, example: 'aprueba' },
                });
                if (row) askApprove(row);
                break;
            }
            case 'complete_action':
            case 'validate_action': {
                const isValidate = intent === 'validate_action';
                const rows = (isValidate ? data.adjustmentsToValidate : data.adjustmentsAssigned) || [];
                const row = resolveTarget(text, rows, {
                    kindOfList: isValidate ? 'ajustes por validar' : 'mis ajustes',
                    label: isValidate ? 'un ajuste por validar' : 'un ajuste asignado',
                    sameItem: (candidate, picked) => candidate.id === picked.id,
                    filter: (list) => filterAdjustments(list, filters, clientOf),
                    listCards: {
                        kind: isValidate ? 'validate' : 'assigned',
                        card: (item) => adjustmentCard(item, isValidate ? 'validate' : 'assigned'),
                        example: isValidate ? 'valida' : 'completa',
                    },
                });
                if (row) askAdjustment(row, isValidate ? 'validate' : 'complete');
                break;
            }
            case 'open_ref': {
                const last = lastListRef.current;
                const picked = pickFromList(last?.items, parseReference(text));
                if (!picked) {
                    pushMessage({
                        from: 'bot',
                        text: last?.items?.length ? `¿Cuál? Hay ${last.items.length} en la lista; dime “el 1”, “el 2”… o “el último”.` : 'Primero pídeme algo (un reporte o tus pendientes) y luego dime cuál abrir.',
                    });
                    break;
                }
                if (last.kind === 'reports') {
                    pushMessage({ from: 'bot', text: `Abriendo “${picked.reporteNombre}”.` });
                    openReportById(picked.id);
                } else if (picked.kind === 'mention' || last.kind === 'mentions') {
                    openNotification(picked);
                } else {
                    pushMessage({ from: 'bot', text: `Abriendo la ficha de “${picked.reportName}”.` });
                    openDetails(picked.reportId);
                }
                break;
            }
            case 'favorites': {
                const top = topUsed(allReports, usage);
                rememberList('reports', top);
                pushMessage({
                    from: 'bot',
                    text: top.length ? 'Los reportes que más abres:' : 'Todavía no sé cuáles usas más; en cuanto abras algunos, los recordaré.',
                    cards: numbered(top.map(reportCard)),
                });
                break;
            }
            case 'mentions': {
                const rows = data.mentions || [];
                rememberList('mentions', rows);
                pushMessage({
                    from: 'bot',
                    text: rows.length ? 'Te mencionaron aquí:' : 'No tienes menciones sin leer.',
                    cards: numbered(rows.map(notificationCard)),
                });
                break;
            }
            case 'notifications': {
                const rows = data.notifications || [];
                rememberList('mentions', rows);
                const actions = userPermissions?.admin_panel_access
                    ? [{ label: 'Ver todas', onClick: () => goTo(() => { setView('admin'); setAdminSubView('notifications'); }) }]
                    : undefined;
                pushMessage({
                    from: 'bot',
                    text: rows.length ? `Tienes ${data.counts.unread} sin leer. Las más recientes:` : 'No tienes notificaciones sin leer.',
                    cards: numbered(rows.map(notificationCard)),
                    actions,
                });
                break;
            }
            case 'account':
                pushMessage({ from: 'bot', text: 'Te llevo a tu cuenta.' });
                goTo(() => setView('account'));
                break;
            case 'home':
                pushMessage({ from: 'bot', text: 'Listo, de vuelta al inicio.' });
                goTo(goHome);
                break;
            case 'creator':
                if (userPermissions?.reports_custom_creator) {
                    pushMessage({ from: 'bot', text: 'Abriendo el Creador de reportes.' });
                    goTo(() => { setView('admin'); setAdminSubView('creador-reportes'); });
                } else {
                    pushMessage({ from: 'bot', text: 'Tu rol no tiene acceso al Creador de reportes. Si lo necesitas, pídeselo a un administrador.' });
                }
                break;
            default: {
                const results = searchReports(allReports, text, 6, usage);
                if (!results.length) {
                    pushMessage({
                        from: 'bot',
                        text: `No encontré reportes con “${truncate(text, 40)}” entre los que puedes ver. Prueba con el nombre del cliente o del proyecto.`,
                    });
                    break;
                }
                rememberList('reports', results);
                pushMessage({
                    from: 'bot',
                    text: results.length === 1 ? 'Encontré este reporte (dime “ábrelo”):' : `Encontré ${results.length} reportes (dime “abre el 1”, “el segundo”…):`,
                    cards: numbered(results.map(reportCard)),
                });
            }
        }
    }, [digest, fetchDigest, pushMessage, allReports, clients, usage, userPermissions, goTo, goHome, setView, setAdminSubView, openReportById, openDetails, openNotification, markRead, impersonation]); // eslint-disable-line react-hooks/exhaustive-deps -- los armadores de tarjetas y confirmaciones se recrean en cada render

    /** Envía una instrucción: Pixel la anota, piensa (nube con engranes) y responde. */
    const send = useCallback(async (raw) => {
        const text = String(raw || '').trim();
        if (!text) return;
        lastInteractionRef.current = Date.now();
        pushMessage({ from: 'user', text });
        setInput('');
        setUserTyping(false);
        setActivity('typing');
        await wait(TYPING_MS);
        setActivity('thinking');
        await wait(THINK_MIN_MS);
        try {
            await reply(text);
        } finally {
            setActivity(null);
        }
    }, [pushMessage, reply]);

    /**
     * Saluda con un solo mensaje en el globo; no abre la conversación.
     * kind: 'login' (al iniciar sesión) o 'return' (al volver a la pantalla).
     */
    const greet = useCallback((data, kind) => {
        if (openRef.current) return;
        const now = new Date();
        if (kind === 'return' && uid && readNudgeState(uid).muteDay === dayKey(now)) return;
        const total = pendingTotal(data);
        const name = displayName ? `, ${displayName}` : '';
        const hello = kind === 'return' ? `¡Hola de nuevo${name}!` : `¡${greetingFor(now)}${name}! Soy Pixel.`;
        setNudge({
            mood: 'wave',
            text: total
                ? `${hello} Tienes ${total} ${total === 1 ? 'pendiente' : 'pendientes'}. ¿Te los muestro?`
                : `${hello} Estás al día. Si buscas un reporte, pregúntame.`,
        });
        /* El saludo cuenta como el aviso del día: no repetir el globo enseguida. */
        if (uid && data) {
            writeNudgeState(uid, { lastGreetDay: dayKey(now), lastRemindAt: now.getTime(), lastSignature: urgentItems(data).signature });
        }
    }, [displayName, uid]);
    greetRef.current = greet;

    /* Sin uso durante IDLE_SLEEP_MS, Pixel cierra la conversación y se duerme. */
    useEffect(() => {
        if (!open) return undefined;
        const timer = window.setInterval(() => {
            const idle = Date.now() - lastInteractionRef.current >= IDLE_SLEEP_MS;
            if (idle && !activity && !input.trim()) {
                pushMessage({ from: 'bot', text: 'Me voy a dormir un rato. Tócame cuando me necesites. 💤' });
                setOpen(false);
            }
        }, IDLE_CHECK_MS);
        return () => window.clearInterval(timer);
    }, [open, activity, input, pushMessage]);

    /** Abre el panel; la primera vez saluda y muestra el resumen. */
    const openPanel = useCallback(async (withSummary = false) => {
        lastInteractionRef.current = Date.now();
        setNudge(null);
        setOpen(true);
        const data = Date.now() - lastFetchRef.current > STALE_MS ? await fetchDigest() : digest;
        if (messages.length === 0) {
            const lines = summaryLines(data);
            pushMessage({
                from: 'bot',
                text: `¡Hola${displayName ? `, ${displayName}` : ''}! Soy Pixel. ${lines.length ? 'Esto tienes pendiente:' : 'Estás al día. ¿Buscas algún reporte?'}`,
                lines,
            });
        } else if (withSummary) {
            reply('mis pendientes');
        }
        window.setTimeout(() => inputRef.current?.focus(), 60);
    }, [fetchDigest, digest, messages.length, pushMessage, displayName, reply]);

    const muteToday = () => {
        writeNudgeState(uid, { muteDay: dayKey() });
        setNudge(null);
    };

    /* El lienzo del Creador ocupa toda la pantalla: ahí la mascota estorba. */
    if (!uid || (view === 'admin' && adminSubView === 'creador-reportes')) return null;

    const badge = pendingTotal(digest);
    const hasUrgent = (digest?.counts?.approvalsOverdue || 0) + (digest?.counts?.mentions || 0) > 0;
    /* En reposo es el orbe del logo; al abrir el panel o avisar algo se transforma en bot. */
    const launcherForm = open || nudge ? 'bot' : 'orb';
    const typingNow = activity === 'typing' || (open && userTyping);
    const thinkingNow = activity === 'thinking';
    const launcherMood = nudge ? nudge.mood : typingNow ? 'typing' : thinkingNow || loading ? 'think' : 'idle';
    const thought = thoughtPlacement(pos, viewport.w);
    /* Dormido: conversación cerrada y sin avisos pendientes. */
    const asleep = !open && !nudge;
    const narrow = viewport.w <= NARROW_MAX;
    /* Con Pixel sobre el menú lateral, el panel y el globo se abren a un lado para no tapar sus botones. */
    const clearLeft = !narrow && dock.sideRight > 0 && pos.x < dock.sideRight ? dock.sideRight + 10 : null;
    const panelPlace = placePopover({ pos, vw: viewport.w, vh: viewport.h, width: 370, maxHeight: 560, clearLeft });
    const nudgePlace = placePopover({ pos, vw: viewport.w, vh: viewport.h, width: 280, maxHeight: 400, clearLeft });
    const fixedAt = ({ left, width, top, bottom }) => ({ left, width, top, bottom });
    /* Móvil: el panel se abre como hoja inferior a todo el ancho. */
    const panelStyle = narrow
        ? {
            ...styles.panel,
            left: 8,
            width: viewport.w - 16,
            top: undefined,
            bottom: 8,
            height: Math.min(Math.round(viewport.h * 0.82), 640),
            borderRadius: 16,
        }
        : { ...styles.panel, ...fixedAt(panelPlace), height: panelPlace.maxHeight };
    /* La esquina más cercana a Pixel queda en punta, como el pico de un globo de diálogo. */
    const tailRadius = ({ above, alignRight }) => {
        const corners = [14, 14, 14, 14];
        corners[above ? (alignRight ? 2 : 3) : (alignRight ? 1 : 0)] = 4;
        return corners.map((r) => r + 'px').join(' ');
    };

    const onLauncherClick = () => {
        if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
        }
        if (open) setOpen(false);
        else openPanel(false);
    };

    return (
        <div style={styles.root}>
            <style>{PIXEL_CSS}{PANEL_CSS}</style>

            {open && (
                <section
                    className="cm-pixel-pop"
                    style={panelStyle}
                    aria-label="Asistente Pixel"
                    onPointerDown={markInteraction}
                    onKeyDown={markInteraction}
                    onWheel={markInteraction}
                >
                    <header style={styles.header}>
                        <PixelMascot form="orb" size={50} />
                        <div style={styles.headerText}>
                            <strong style={styles.headerTitle}>Pixel</strong>
                            <span style={styles.headerSub}>
                                {typingNow ? 'Anotando…' : thinkingNow ? 'Pensando…' : loading ? 'Revisando tus pendientes…' : 'Tu asistente de reportes'}
                            </span>
                        </div>
                        <button type="button" onClick={() => setOpen(false)} style={styles.closeBtn} aria-label="Cerrar asistente">×</button>
                    </header>

                    <div ref={scrollRef} style={styles.messages}>
                        {messages.map((message) => (
                            <MessageBubble key={message.id} message={message} onPick={pick} />
                        ))}
                        {thinkingNow && (
                            <div style={styles.msgRow}>
                                <div style={styles.botBubble}>
                                    <p style={{ ...styles.msgText, color: ink.muted }} className="cm-pixel-thinking">Pixel está pensando</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="cm-pixel-chips" style={styles.chips}>
                        {QUICK_CHIPS.map((chip) => (
                            <button key={chip} type="button" className="cm-pixel-chip" style={styles.chip} onClick={() => send(chip)}>
                                {chip}
                            </button>
                        ))}
                    </div>

                    <form
                        style={styles.inputRow}
                        onSubmit={(event) => { event.preventDefault(); send(input); }}
                    >
                        <input
                            ref={inputRef}
                            value={input}
                            onChange={onInputChange}
                            placeholder="Busca un reporte o pregúntame…"
                            style={styles.input}
                            aria-label="Mensaje para Pixel"
                        />
                        <button type="submit" style={styles.sendBtn} disabled={!input.trim()}>Enviar</button>
                    </form>
                </section>
            )}

            {!open && nudge && (
                <div className="cm-pixel-pop" style={{ ...styles.nudge, ...fixedAt(nudgePlace), borderRadius: tailRadius(nudgePlace) }} role="status">
                    <button type="button" onClick={() => setNudge(null)} style={styles.nudgeClose} aria-label="Cerrar aviso">×</button>
                    <p style={styles.nudgeText}>{nudge.text}</p>
                    <div style={styles.nudgeActions}>
                        <button type="button" className="cm-pixel-act" style={styles.actPrimary} onClick={() => openPanel(true)}>Ver pendientes</button>
                        <button type="button" className="cm-pixel-act" style={styles.act} onClick={muteToday}>Silenciar hoy</button>
                    </div>
                </div>
            )}

            <button
                type="button"
                className="cm-pixel-launcher"
                style={{
                    ...styles.launcher,
                    left: pos.x,
                    top: pos.y,
                    cursor: dragging ? 'grabbing' : 'grab',
                    ...(narrow && open ? { visibility: 'hidden' } : null),
                }}
                onClick={onLauncherClick}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onKeyDown={onLauncherKeyDown}
                aria-label={badge ? `Abrir Pixel, ${badge} pendientes` : 'Abrir Pixel'}
                title={asleep ? 'Pixel está dormido · clic para despertarlo, arrastra para moverlo' : 'Pixel · clic para abrir, arrastra para moverlo'}
                data-docked={docked ? 'true' : 'false'}
            >
                <PixelMascot
                    form={launcherForm}
                    mood={launcherMood}
                    urgent={hasUrgent}
                    gaze={gaze}
                    sleeping={asleep}
                    thinking={thinkingNow}
                    thoughtSide={thought.side}
                    thoughtLow={thought.low}
                    size={70}
                />
                {badge > 0 && <span style={{ ...styles.badge, ...(hasUrgent ? styles.badgeUrgent : {}) }}>{badge > 99 ? '99+' : badge}</span>}
            </button>
        </div>
    );
}

function MessageBubble({ message, onPick = (fn) => fn() }) {
    const isBot = message.from === 'bot';
    return (
        <div style={{ ...styles.msgRow, justifyContent: isBot ? 'flex-start' : 'flex-end' }}>
            <div style={isBot ? styles.botBubble : styles.userBubble}>
                {message.text && <p style={styles.msgText}>{message.text}</p>}
                {message.lines?.length > 0 && (
                    <ul style={styles.lines}>
                        {message.lines.map((line) => <li key={line}>{line}</li>)}
                    </ul>
                )}
                {message.cards?.length > 0 && (
                    <div style={styles.cards}>
                        {message.cards.map((card) => (
                            <div key={card.key} style={{ ...styles.card, ...(TONES[card.tone] || {}) }}>
                                <div style={styles.cardTitle}>{card.title}</div>
                                {card.subtitle && <div style={styles.cardSub}>{card.subtitle}</div>}
                                {card.actions?.length > 0 && (
                                    <div style={styles.cardActions}>
                                        {card.actions.map((action, index) => (
                                            <button
                                                key={action.label}
                                                type="button"
                                                className="cm-pixel-act"
                                                style={index === 0 ? styles.actPrimary : styles.act}
                                                onClick={() => onPick(action.onClick)}
                                            >
                                                {action.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {message.actions?.length > 0 && (
                    <div style={styles.cardActions}>
                        {message.actions.map((action) => (
                            <button key={action.label} type="button" className="cm-pixel-act" style={action.primary ? styles.actPrimary : styles.act} onClick={() => onPick(action.onClick)}>
                                {action.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

const TONES = {
    danger: { borderLeftColor: ink.danger, backgroundColor: ink.dangerSoft },
    warn: { borderLeftColor: '#e37400', backgroundColor: '#fef7e0' },
    accent: { borderLeftColor: ink.accent, backgroundColor: ink.accentSoft },
    plain: {},
};

const buttonBase = {
    fontFamily: 'inherit',
    cursor: 'pointer',
    borderRadius: 8,
    fontSize: '0.78rem',
    fontWeight: 600,
    padding: '5px 10px',
};

const styles = {
    root: {
        position: 'fixed',
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        zIndex: 900,
        fontFamily: 'inherit',
    },
    launcher: {
        position: 'fixed',
        width: LAUNCHER_SIZE,
        height: LAUNCHER_SIZE,
        touchAction: 'none',
        userSelect: 'none',
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        filter: 'drop-shadow(0 6px 14px rgba(36, 22, 64, 0.28))',
    },
    badge: {
        position: 'absolute',
        top: 2,
        right: 0,
        minWidth: 18,
        height: 18,
        padding: '0 4px',
        borderRadius: 10,
        background: ink.accent,
        color: '#fff',
        fontSize: '0.7rem',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #fff',
        boxSizing: 'border-box',
    },
    badgeUrgent: { background: ink.danger },
    nudge: {
        position: 'fixed',
        boxSizing: 'border-box',
        padding: '12px 14px 12px',
        background: ink.surface,
        border: `1px solid ${ink.line}`,
        boxShadow: '0 10px 30px rgba(36, 22, 64, 0.18)',
        color: ink.text,
    },
    nudgeClose: {
        position: 'absolute',
        top: 4,
        right: 6,
        border: 'none',
        background: 'transparent',
        color: ink.faint,
        fontSize: '1.1rem',
        cursor: 'pointer',
        lineHeight: 1,
    },
    nudgeText: { margin: '0 14px 10px 0', fontSize: '0.88rem', lineHeight: 1.45 },
    nudgeActions: { display: 'flex', gap: 6, flexWrap: 'wrap' },
    panel: {
        position: 'fixed',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        background: ink.surface,
        border: `1px solid ${ink.line}`,
        borderRadius: 18,
        boxShadow: '0 18px 48px rgba(36, 22, 64, 0.24)',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        background: 'linear-gradient(135deg, #1b1528 0%, #3a2566 100%)',
        color: '#fff',
    },
    headerText: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
    headerTitle: { fontSize: '0.98rem', letterSpacing: '-0.01em' },
    headerSub: { fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' },
    closeBtn: {
        border: 'none',
        background: 'rgba(255,255,255,0.12)',
        color: '#fff',
        width: 28,
        height: 28,
        borderRadius: 8,
        cursor: 'pointer',
        fontSize: '1.1rem',
        lineHeight: 1,
    },
    messages: {
        flex: 1,
        overflowY: 'auto',
        padding: '14px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        background: ink.wash,
    },
    msgRow: { display: 'flex' },
    botBubble: {
        maxWidth: '92%',
        padding: '10px 12px',
        background: ink.surface,
        border: `1px solid ${ink.line}`,
        borderRadius: '4px 14px 14px 14px',
        color: ink.text,
    },
    userBubble: {
        maxWidth: '80%',
        padding: '8px 12px',
        background: ink.accent,
        border: `1px solid ${ink.accent}`,
        borderRadius: '14px 4px 14px 14px',
        color: '#fff',
    },
    msgText: { margin: 0, fontSize: '0.87rem', lineHeight: 1.45, whiteSpace: 'pre-wrap' },
    lines: { margin: '6px 0 0', paddingLeft: 18, fontSize: '0.84rem', lineHeight: 1.5, color: ink.text },
    cards: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 },
    card: {
        padding: '8px 10px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderLeftWidth: 3,
        borderLeftColor: ink.lineStrong,
        borderRadius: 10,
        backgroundColor: ink.surface,
    },
    cardTitle: { fontSize: '0.84rem', fontWeight: 600, color: ink.text, lineHeight: 1.35 },
    cardSub: { fontSize: '0.76rem', color: ink.muted, marginTop: 2, lineHeight: 1.4 },
    cardActions: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 },
    act: {
        ...buttonBase,
        background: ink.surface,
        color: ink.accentText,
        border: `1px solid ${ink.lineStrong}`,
    },
    actPrimary: {
        ...buttonBase,
        background: ink.accent,
        color: '#fff',
        border: `1px solid ${ink.accent}`,
    },
    chips: {
        display: 'flex',
        gap: 6,
        overflowX: 'auto',
        padding: '8px 12px 4px',
        borderTop: `1px solid ${ink.line}`,
        background: ink.surface,
    },
    chip: {
        ...buttonBase,
        flex: '0 0 auto',
        fontWeight: 500,
        borderRadius: 999,
        padding: '5px 11px',
        background: ink.surface,
        color: ink.accentText,
        border: `1px solid ${ink.lineStrong}`,
        whiteSpace: 'nowrap',
    },
    inputRow: { display: 'flex', gap: 8, padding: '8px 12px 12px', background: ink.surface },
    input: {
        flex: 1,
        minWidth: 0,
        padding: '9px 12px',
        fontSize: '0.87rem',
        fontFamily: 'inherit',
        border: `1px solid ${ink.lineStrong}`,
        borderRadius: 10,
        outline: 'none',
        color: ink.text,
    },
    sendBtn: {
        ...buttonBase,
        fontSize: '0.84rem',
        padding: '8px 14px',
        background: ink.accent,
        color: '#fff',
        border: `1px solid ${ink.accent}`,
        borderRadius: 10,
    },
};
